<template>
  <dx-modal
    v-if="show"
    :value="show"
    @input="$emit('close')"
    :title="null"
    size="md"
    class="license-confirmation-modal"
  >
    <h5 class="header-title">Confirmar Alteração de Licenças</h5>

    <div class="divider" />

    <div class="comparison-section">
      <div class="comparison-item">
        <div class="comparison-label">Atual</div>
        <div class="comparison-value">
          <div class="value-number">{{ contract.totalLicenses }}</div>
          <div class="value-text">licenças</div>
        </div>
        <div class="comparison-price">
          {{ contract.totalValue | currency }}/mês<br>
          <small class="text-bold text-dark-gray">
            {{ contract.discountPercentage > 0 ? `${contract.discountPercentage}% OFF` : '' }}
          </small>
        </div>
      </div>

      <div class="comparison-arrow">
        <fa-icon :icon="['fal', 'arrow-right']" />
      </div>

      <div class="comparison-item highlighted">
        <div class="comparison-label">Novo</div>
        <div class="comparison-value">
          <div class="value-number">{{ contract.newQuantity }}</div>
          <div class="value-text">licenças</div>
        </div>
        <div class="comparison-price">
          {{ newValue | currency }}/mês<br>
          <small class="text-bold text-dark-gray">
            {{ appliedDiscountPercentage > 0 ? `${appliedDiscountPercentage}% OFF` : '' }}
          </small>
        </div>
      </div>
    </div>

    <div
      v-if="difference !== 0"
      class="impact-badge"
      :class="difference > 0 ? 'is-increase' : 'is-decrease'"
    >
      <fa-icon :icon="['fal', difference > 0 ? 'arrow-up' : 'arrow-down']" />
      <span>
        {{ difference > 0 ? 'Aumento de' : 'Economia de' }}
        <strong>{{ Math.abs(difference) | currency }}/mês</strong>
      </span>
    </div>

    <div class="alert-info">
      <fa-icon :icon="['fal', 'info-circle']" />
      <span v-if="difference < 0">
        A redução passa a valer no próximo ciclo de cobrança. A fatura que está por fechar
        mantém a quantidade atual, referente ao período já utilizado.
      </span>
      <span v-else>A alteração será aplicada imediatamente e refletida na próxima fatura.</span>
    </div>

    <template slot="footer">
      <button
        class="btn btn-primary btn-icon btn-icon-left mr-1"
        :class="{ loading: saving }"
        :disabled="saving"
        @click="save"
      >
        <fa-icon :icon="['fal', 'check-circle']" />
        Confirmar Alteração
      </button>
      <button class="btn" @click="$emit('close')">
        Cancelar
      </button>
    </template>
  </dx-modal>
</template>

<script>
const MAX_DISCOUNT = 15;

const DISCOUNT_TIERS = [
  { from: 1, to: 9, percentage: 0 },
  { from: 10, to: 19, percentage: 3 },
  { from: 20, to: 29, percentage: 6 },
  { from: 30, to: 39, percentage: 9 },
  { from: 40, to: 49, percentage: 12 },
  { from: 50, to: 99, percentage: 15 },
];

function findTier(quantity) {
  if (quantity > 99) return { percentage: MAX_DISCOUNT };
  return DISCOUNT_TIERS.find(({ from, to }) => quantity >= from && quantity <= to)
    || { percentage: 0 };
}

export default {
  name: 'LicenseConfirmationModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    contract: {
      totalLicenses: {
        type: Number,
        required: true,
      },
      unitValue: {
        type: Number,
        required: true,
      },
      totalValue: {
        type: Number,
        required: true,
      },
      discountPercentage: {
        type: Number,
        default: 0,
      },
      newQuantity: {
        type: Number,
        required: true,
      },
    },
  },
  computed: {
    currentTier() {
      return findTier(this.contract.totalLicenses);
    },
    newTier() {
      return findTier(this.contract.newQuantity);
    },
    appliedDiscountPercentage() {
      if (this.currentTier !== this.newTier) {
        return this.newTier.percentage;
      }
      return Math.max(this.newTier.percentage, this.contract.discountPercentage || 0);
    },
    newValue() {
      return this.contract.newQuantity
        * this.contract.unitValue
        * (1 - this.appliedDiscountPercentage / 100);
    },
    difference() {
      return this.newValue - this.contract.totalValue;
    },
  },
  methods: {
    save() {
      this.$emit('confirm', {
        newValue: this.newValue,
        newTier: this.newTier,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.license-confirmation-modal {
  .divider {
    margin: $layout-spacing-xl 0;
  }

  .comparison-section {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: $layout-spacing-lg * 1.5;
    align-items: center;
    margin-bottom: $layout-spacing-lg * 1.5;

    @media (max-width: $size-sm) {
      grid-template-columns: 1fr;
      gap: $layout-spacing-lg;
    }

    .comparison-item {
      text-align: center;
      padding: $layout-spacing-lg * 1.5;
      background: $bg-color;
      border: 1px solid $border-color;
      border-radius: 10px;
      transition: all 0.3s ease;

      &.highlighted {
        background: linear-gradient(
          135deg,
          lighten($primary-color, 45%) 0%,
          lighten($primary-color, 48%) 100%
        );
        border-color: $primary-color;
        box-shadow: 0 2px 8px rgba($primary-color, 0.15);
      }

      .comparison-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: $gray-color-dark;
        margin-bottom: $layout-spacing-lg;
      }

      .comparison-value {
        margin-bottom: $layout-spacing;

        .value-number {
          font-size: 48px;
          font-weight: 800;
          color: $dark-color;
          line-height: 1;
          margin-bottom: $layout-spacing-sm;
        }

        .value-text {
          font-size: 13px;
          color: $gray-color-dark;
          font-weight: 600;
        }
      }

      .comparison-price {
        font-size: 16px;
        font-weight: 700;
        color: $dark-color;
      }
    }

    .comparison-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      color: $primary-color;
      font-size: 24px;

      @media (max-width: $size-sm) {
        transform: rotate(90deg);
      }
    }
  }

  .impact-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $layout-spacing-sm;
    padding: $layout-spacing $layout-spacing-lg;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: $layout-spacing-lg * 1.5;

    &.is-increase {
      background: lighten($error-color, 45%);
      color: darken($error-color, 15%);
      border: 1px solid lighten($error-color, 30%);
    }

    &.is-decrease {
      background: lighten($success-color, 45%);
      color: darken($success-color, 15%);
      border: 1px solid lighten($success-color, 30%);
    }

    strong {
      font-weight: 700;
    }
  }

  .alert-info {
    display: flex;
    align-items: center;
    gap: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
    background: lighten($primary-color, 47%);
    border-left: 3px solid $primary-color;
    border-radius: 4px;
    font-size: 13px;
    color: darken($primary-color, 10%);
    line-height: 1.5;

    svg {
      flex-shrink: 0;
      color: $primary-color;
    }
  }
}
</style>
